import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import {
  countDadosCadastrais,
  formatDocument,
  formatToReal,
} from "../../../utils/utils";
import { LuBuilding2 } from "react-icons/lu";
import { FiPhone, FiUserCheck } from "react-icons/fi";
import { MdOutlineEmail } from "react-icons/md";
import { HiOutlineLocationMarker } from "react-icons/hi";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const hasOwnKey = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj || {}, key);

const BR_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const getDisplayDate = (value) => {
  if (!value) return null;
  const dateStr = String(value).trim();
  if (BR_DATE_REGEX.test(dateStr)) return dateStr;
  const parsed = moment(dateStr, [moment.ISO_8601, "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : null;
};

const getMomentDate = (value) => {
  if (!value) return null;
  const dateStr = String(value).trim();
  if (BR_DATE_REGEX.test(dateStr)) {
    const parsedBr = moment(dateStr, "DD/MM/YYYY", true);
    return parsedBr.isValid() ? parsedBr : null;
  }
  const parsed = moment(dateStr, [moment.ISO_8601, "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed : null;
};

const CadastralPFQuery = ({ data, dataFull }) => {
  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 3;
  const cadastro = data?.dadosCadastrais || {};
  const birthMoment = getMomentDate(cadastro.dataNascimento);
  const birthLabel = getDisplayDate(cadastro.dataNascimento);
  const birthAgeLabel = buildDurationLabel(birthMoment);
  const statusLabel =
    (hasOwnKey(cadastro, "situacaoCadastral")
      ? cadastro.situacaoCadastral
      : cadastro.situacao) || "";

  const boolLabel = (value) => {
    if (value === true) return "Sim";
    if (value === false) return "Não";
    return "Não informado";
  };

  const situacaoDataMoment = hasOwnKey(cadastro, "dataSituacaoCadastral")
    ? moment(cadastro.dataSituacaoCadastral, ["DD/MM/YYYY", "YYYY-MM-DD"], true)
    : null;
  const dataSituacaoLabel =
    situacaoDataMoment && situacaoDataMoment.isValid()
      ? situacaoDataMoment.format("DD/MM/YYYY")
      : "Não informado";

  const hasRendaEstimadaKey = hasOwnKey(cadastro, "rendaEstimada");
  const hasRendaFaixaKey = hasOwnKey(cadastro, "rendaFaixaSalarial");
  const hasClasseSocialKey = hasOwnKey(cadastro, "classeSocial");
  const hasSocioeconomicoGroup =
    hasRendaEstimadaKey || hasRendaFaixaKey || hasClasseSocialKey;

  const additionalCards = [
    hasOwnKey(cadastro, "idade") && {
      label: "Idade",
      value:
        cadastro.idade === null || cadastro.idade === undefined
          ? "Não informado"
          : cadastro.idade,
      hint: "Anos",
    },
    hasOwnKey(cadastro, "nomePai") && {
      label: "Nome do pai",
      value: cadastro.nomePai || "Não informado",
      hint: "Parente",
    },
    hasOwnKey(cadastro, "obito") && {
      label: "Óbito",
      value: boolLabel(cadastro.obito),
      hint: "Status",
    },
    hasOwnKey(cadastro, "cbo") && {
      label: "CBO",
      value: cadastro.cbo || "Não informado",
      hint: "Profissão",
    },
    hasOwnKey(cadastro, "situacaoCadastral") && {
      label: "Situação cadastral",
      value: cadastro.situacaoCadastral || "Não informado",
      hint: "Cadastro",
    },
  ].filter(Boolean);

  const hasParentescosKey =
    hasOwnKey(cadastro, "parentescos") || hasOwnKey(data, "parentescos");
  const parentescos = Array.isArray(cadastro.parentescos)
    ? cadastro.parentescos
    : Array.isArray(data?.parentescos)
      ? data.parentescos
      : [];
  const summaryCards = [
    {
      label: "CPF",
      value: formatDocument(cadastro.cpf) || "Não disponível",
      hint: "Documento",
    },
    {
      label: "Nome da mãe",
      value: cadastro.nomeMae || "Não informado",
      hint: "Parente",
    },
    {
      label: "Sexo",
      value: cadastro.sexo,
      hint: "Gênero",
    },
    {
      label: "Nascimento",
      value: birthLabel || "Não informado",
      hint: "Data",
    },
  ];

  return (
    <div>
      {podeRenderizarDadosCadastrais && (
        <>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiUserCheck className="text-blue-700" />{" "}
              <span>Dados Cadastrais</span>
            </div>
          </TitleStripe>
          <div className="mt-4 text-xs">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  Nome
                </p>
                <p className="text-xl font-semibold text-gray-900">
                  {cadastro.nome || "Indisponível"}
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Situação: {statusLabel || "Desconhecida"}
              </span>
              {birthLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  Nasc: {birthLabel}
                </span>
              )}
              {birthAgeLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  {birthAgeLabel}
                </span>
              )}
            </div>

            <div className="mt-3 grid gap-2 grid-cols-4">
              {summaryCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>

            {additionalCards.length > 0 && (
              <div className="mt-3 grid gap-2 grid-cols-4">
                {additionalCards.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2"
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      {item.label}
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.value}
                    </p>
                    <p className="text-[10px] text-gray-500">{item.hint}</p>
                  </div>
                ))}
              </div>
            )}

            {hasSocioeconomicoGroup && (
              <div className="mt-3 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">
                    Perfil Socioeconômico
                  </p>
                  <span className="rounded-full border border-emerald-200 bg-white/80 px-2 py-0.5 text-[10px] text-emerald-700">
                    Dados financeiros
                  </span>
                </div>
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                  As informações de perfil socioeconômico são aproximadas e
                  podem variar conforme a fonte consultada.
                </p>
                <div className="mt-3 grid gap-2 grid-cols-3">
                  {hasRendaEstimadaKey && (
                    <div className="rounded-xl border border-emerald-200/70 bg-white/90 px-3 py-2 shadow-sm">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600">
                        Renda estimada
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {cadastro.rendaEstimada
                          ? formatToReal(cadastro.rendaEstimada)
                          : "Não informado"}
                      </p>
                    </div>
                  )}
                  {hasRendaFaixaKey && (
                    <div className="rounded-xl border border-teal-200/70 bg-white/90 px-3 py-2 shadow-sm">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-teal-600">
                        Faixa salarial
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {cadastro.rendaFaixaSalarial || "Não informado"}
                      </p>
                    </div>
                  )}
                  {hasClasseSocialKey && (
                    <div className="rounded-xl border border-cyan-200/70 bg-white/90 px-3 py-2 shadow-sm">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-700">
                        Classe social
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {cadastro.classeSocial || "Não informado"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {hasParentescosKey && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiUserCheck className="text-blue-700" />
              <span>Pessoas relacionadas</span>
            </div>
          </TitleStripe>
          {parentescos.length > 0 ? (
            <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-slate-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">Grau</th>
                    <th className="px-3 py-2 font-medium">Nome</th>
                    <th className="px-3 py-2 font-medium">CPF</th>
                    <th className="px-3 py-2 font-medium">Nascimento</th>
                    <th className="px-3 py-2 font-medium">Gênero</th>
                    <th className="px-3 py-2 font-medium">Óbito</th>
                    <th className="px-3 py-2 font-medium">Data óbito</th>
                  </tr>
                </thead>
                <tbody>
                  {parentescos.map((par, index) => {
                    const parBirthLabel = getDisplayDate(par?.dataNascimento);
                    const parObitoMoment = par?.dataObito
                      ? moment(
                          par.dataObito,
                          ["DD/MM/YYYY", "YYYY-MM-DD"],
                          true,
                        )
                      : null;

                    return (
                      <tr
                        key={`${par?.cpf || "sem-cpf"}-${index}`}
                        className={
                          index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                        }
                      >
                        <td className="px-3 py-3">
                          {par?.grauParentesco || "-"}
                        </td>
                        <td className="px-3 py-3">{par?.nome || "-"}</td>
                        <td className="px-3 py-3">
                          {formatDocument(par?.cpf) || "-"}
                        </td>
                        <td className="px-3 py-3">{parBirthLabel || "-"}</td>
                        <td className="px-3 py-3">{par?.genero || "-"}</td>
                        <td className="px-3 py-3">{boolLabel(par?.obito)}</td>
                        <td className="px-3 py-3">
                          {parObitoMoment && parObitoMoment.isValid()
                            ? parObitoMoment.format("DD/MM/YYYY")
                            : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.participacaoEmEmpresas && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <LuBuilding2 className="text-blue-700" />
              <span>Participação em empresas</span>
            </div>
          </TitleStripe>
          {data?.participacaoEmEmpresas?.length > 0 ? (
            <ParticipacaoEmEmpresas empresas={data?.participacaoEmEmpresas} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <HiOutlineLocationMarker className="text-blue-700" />{" "}
              <span>Histórico de Endereços</span>
            </div>
          </TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <MdOutlineEmail className="text-blue-700" />{" "}
              <span>Histórico de E-mails</span>
            </div>
          </TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiPhone className="text-blue-700" />{" "}
              <span>Histórico de Telefones</span>
            </div>
          </TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </div>
  );
};

export default CadastralPFQuery;
